import api from '@/api'
import authToken from '@/functions/authToken'
import config from '@/config'
import is from '@/functions/is'
import LoadingOverlay from '@/functions/LoadingOverlay'
import store from '@/store'

/**
 * Before each guard
 * @param {Object} to vue-router to
 * @param {Object} from vue-router from
 * @param {Function} next vue-router next
 */
export default async function (to, from, next) {
  const token = authToken.get()

  // Reset global alert before each route change
  store.commit('global/alert', { show: false, title: '', type: '' })

  let user = store.state.user

  if (token) {
    const loadingOverlay = new LoadingOverlay()

    try {
      loadingOverlay.start()
      user = await api.user.getProfile()
      loadingOverlay.stop()
    } catch (error) {
      loadingOverlay.stop()

      // Don’t force logout if there is trouble connecting to the API
      if (
        error.message === 'Network Error' ||
        (error.response && error.response.status === 500)
      ) {
        store.commit('global/alert', {
          show: true,
          title: '„Coaching für Schulleitungen“ kann zurzeit leider nicht geladen werden. Bitte versuchen Sie es später erneut.',
          type: 'error'
        })
        return
      }

      // Log out if something was wrong with the response
      await api.auth.logout()
      next({ name: 'login' })
      return
    }
  }

  // Log out if mail isn’t confirmed
  if (user.meta.isConfirmed === false) {
    store.commit('global/alert', {
      show: true,
      title: user.meta.isConfirmed === false
        ? 'Bitte bestätigen Sie Ihre E-Mail-Adresse.'
        : 'Ihr Konto wurde noch nicht vom Landesinstitut freigegeben.',
      type: 'error'
    })

    await api.auth.logout()
    next({ name: 'login' })
    return
  }

  // Handle edge-case “user without group”
  if (!user.meta.group) {
    store.commit('global/alert', {
      show: true,
      title: 'Beim Anmelden mit Ihrem Konto sind Probleme aufgetreten. Bitte wenden Sie sich an die Claussen-Simon-Stiftung.',
      type: 'error'
    })

    await api.auth.logout()
    next({ name: 'login' })
    return
  }

  // Hold user hostage unteil they confirm their email address
  if (user.meta.isApproved === false && to.name !== 'signup-pending') {
    next({ name: 'signup-pending' })
    return
  }

  // Hold user hostage until they create their profile
  if (is([config.groups.NEW]) && to.name !== 'signup-profile') {
    next({ name: 'signup-profile' })
    return
  }

  // Approved users shouldn’t see the signup pending page
  if (
    is([config.groups.COACH, config.groups.COACHEE]) &&
    user.meta.isApproved && to.name === 'signup-pending'
  ) {
    next({ name: 'index' })
    return
  }

  // Allow access to desired route if it has no auth
  // or user is in whitelisted group
  if (!to.meta.auth || is(to.meta.auth)) {
    next()
    return
  }

  // Save current route to store for later redirect once the user logged in
  // and has correct permissions
  store.commit('global/redirect', to.fullPath)

  // Redirect to default route for user group
  // if user is not whitelisted for current route
  if (to.name !== config.defaultRoute[user.meta.group]) {
    next({ name: config.defaultRoute[user.meta.group] })
  }
}
